import React from 'react'
import { Card, Input, Space, Button, Select, Spin } from 'antd'
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons'
import { useUsers } from '../hooks/useUsers'
import { useUserActions } from '../hooks/useUserActions'
import { useExport } from '../../../shared/hooks/useExport'
import { UsersTable } from '../components/UsersTable'
import { UserFormModal } from '../components/UserFormModal'
import { BulkActionToolbar } from '../../../shared/components/BulkActionToolbar'
import { useAuthStore } from '../../../auth/store/auth-store'

const { Option } = Select

export const UsersScreen: React.FC = () => {
    const {
        users,
        totalUsers,
        searchText, setSearchText,
        statusFilter, setStatusFilter,
        page, setPage,
        pageSize, setPageSize,
        isLoading,
        isFetching
    } = useUsers()

    const { hasPermission } = useAuthStore()
    const canSuspend = hasPermission('users.suspend')
    const canDelete = hasPermission('users.delete')

    const { exportToCSV } = useExport()

    const {
        isFormModalOpen,
        editingUser,
        handleEditUser,
        handleSuspendUser,
        handleDeleteUser,
        handleBulkSuspend: handleBulkSuspendAction,
        handleBulkDelete: handleBulkDeleteAction,
        handleFormSubmit,
        closeFormModal
    } = useUserActions()

    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])

    const handleSelectionChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys)
    }

    const handleClearSelection = () => {
        setSelectedRowKeys([])
    }

    const handleBulkDelete = () => {
        handleBulkDeleteAction(selectedRowKeys, () => {
            setSelectedRowKeys([])
        })
    }

    const handleBulkSuspend = () => {
        handleBulkSuspendAction(selectedRowKeys, () => {
            setSelectedRowKeys([])
        })
    }

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Search users..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 300 }}
                    />
                    <Select
                        placeholder="Filter by status"
                        style={{ width: 150 }}
                        value={statusFilter}
                        onChange={setStatusFilter}
                    >
                        <Option value="all">All Status</Option>
                        <Option value="active">Active</Option>
                        <Option value="suspended">Suspended</Option>
                        <Option value="blocked">Blocked</Option>
                    </Select>
                    <Button icon={<DownloadOutlined />} onClick={() => exportToCSV(users, 'users-export')}>
                        Export
                    </Button>
                </Space>

                <Spin spinning={isLoading || isFetching}>
                    <UsersTable
                        users={users}
                        loading={isLoading}
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: totalUsers,
                            onChange: (p, ps) => {
                                setPage(p)
                                setPageSize(ps)
                            }
                        }}
                        onEdit={handleEditUser}
                        onSuspend={handleSuspendUser}
                        onDelete={handleDeleteUser}
                        selectedRowKeys={selectedRowKeys}
                        onSelectionChange={handleSelectionChange}
                        canSuspend={canSuspend}
                        canDelete={canDelete}
                    />
                </Spin>
            </Card>

            <BulkActionToolbar
                selectedCount={selectedRowKeys.length}
                onClearSelection={handleClearSelection}
                actions={
                    <>
                        {canSuspend && <Button onClick={handleBulkSuspend}>Suspend Selected</Button>}
                        {canDelete && <Button danger onClick={handleBulkDelete}>Delete Selected</Button>}
                    </>
                }
            />

            <UserFormModal
                open={isFormModalOpen}
                onCancel={closeFormModal}
                onSubmit={handleFormSubmit}
                initialValues={editingUser}
            />
        </Space>
    )
}
